if (typeof ScrollingText !== "function") {
  class ScrollingText extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Defer setup until idle so we don't block initial page interactivity
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.init(), { timeout: 1000 });
      } else {
        setTimeout(() => this.init(), 1);
      }
    }

    disconnectedCallback() {
      if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
      if (this._observer) this._observer.disconnect();
    }

    init() {
      const inner = this.children[0];
      if (!inner) return;

      this._inner = inner;
      this._direction = this.dataset.scrollingDirection || 'ltr';
      this._pauseOnHover = this.dataset.pauseOnHover === 'true';

      // Speed: pixels per second (matches original behaviour: smaller value = slower)
      const baseSpeed = parseInt(this.dataset.scrollingSpeed, 10) || 100;
      this._speed = window.innerWidth > 768 ? baseSpeed : baseSpeed / 1.5;

      this.setupClones();
      this.applyAnimation();
      this.attachListeners();
    }

    setupClones() {
      // Measure once. Clone enough copies to fill the visible area.
      const boxWidth = this.getBoundingClientRect().width;
      const innerWidth = this._inner.getBoundingClientRect().width;
      if (innerWidth === 0) return;

      const cloneCount = Math.ceil(boxWidth / innerWidth) + 1;
      const existing = this.children.length;

      // Add clones if needed
      if (cloneCount > existing) {
        const fragment = document.createDocumentFragment();
        for (let i = existing; i < cloneCount; i++) {
          fragment.appendChild(this._inner.cloneNode(true));
        }
        this.appendChild(fragment);
      } else if (cloneCount < existing) {
        // Remove extras
        for (let i = existing - 1; i >= cloneCount; i--) {
          this.removeChild(this.children[i]);
        }
      }

      // Wrap children in a single track for clean CSS animation
      if (!this.querySelector('.scrolling-text__track')) {
        const track = document.createElement('div');
        track.className = 'scrolling-text__track';
        while (this.firstChild) {
          track.appendChild(this.firstChild);
        }
        this.appendChild(track);
        this._track = track;
      }
    }

applyAnimation() {
      if (!this._track) return;

      const innerWidth = this._track.children[0].getBoundingClientRect().width;
      if (innerWidth === 0) return;

      // Duration = distance / speed. We translate by one inner width per cycle.
      const duration = innerWidth / this._speed;

      // Set the per-instance distance as a CSS variable, then reference it in the keyframes.
      this._track.style.cssText = `
        display: inline-flex;
        will-change: transform;
        --scroll-distance: ${innerWidth}px;
        animation: scrolling-text-${this._direction} ${duration}s linear infinite;
      `;

      // Inject keyframes once, globally
      if (!document.getElementById('scrolling-text-keyframes')) {
        const style = document.createElement('style');
        style.id = 'scrolling-text-keyframes';
        style.textContent = `
          @keyframes scrolling-text-ltr {
            from { transform: translateX(0); }
            to { transform: translateX(calc(var(--scroll-distance) * -1)); }
          }
          @keyframes scrolling-text-rtl {
            from { transform: translateX(calc(var(--scroll-distance) * -1)); }
            to { transform: translateX(0); }
          }
          scrolling-text .scrolling-text__track {
            white-space: nowrap;
          }
          scrolling-text.is-paused .scrolling-text__track {
            animation-play-state: paused !important;
          }
          @media (prefers-reduced-motion: reduce) {
            scrolling-text .scrolling-text__track {
              animation: none !important;
            }
          }
        `;
        document.head.appendChild(style);
      }
    }

    attachListeners() {
      // Pause on hover (if enabled)
      if (this._pauseOnHover) {
        this.addEventListener('mouseenter', () => this.classList.add('is-paused'));
        this.addEventListener('mouseleave', () => {
          if (document.hasFocus()) this.classList.remove('is-paused');
        });
      }

      // Pause when window loses focus
      window.addEventListener('blur', () => this.classList.add('is-paused'));
      window.addEventListener('focus', () => {
        if (!this._pauseOnHover || !this.matches(':hover')) {
          this.classList.remove('is-paused');
        }
      });

      // Pause when off-screen (saves CPU on long pages)
      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!this._pauseOnHover || !this.matches(':hover')) {
                this.classList.remove('is-paused');
              }
            } else {
              this.classList.add('is-paused');
            }
          });
        });
        this._observer.observe(this);
      }

      // Recalculate on actual window resize (debounced, not every frame)
      let resizeTimer;
      this._resizeHandler = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const baseSpeed = parseInt(this.dataset.scrollingSpeed, 10) || 100;
          this._speed = window.innerWidth > 768 ? baseSpeed : baseSpeed / 1.5;

          // Re-measure and re-clone if needed
          const track = this._track;
          if (track) {
            // Reset: pull children back out of the track to re-run setupClones
            while (track.firstChild) {
              this.insertBefore(track.firstChild, track);
            }
            this.removeChild(track);
            this._track = null;

            this.setupClones();
            this.applyAnimation();
          }
        }, 200);
      };
      window.addEventListener('resize', this._resizeHandler);
    }
  }

  if (typeof customElements.get('scrolling-text') === 'undefined') {
    customElements.define('scrolling-text', ScrollingText);
  }
}